import type React from "react"
import { useTranslation } from "react-i18next"

import { EnergyUnitDecimal } from "@/components/decimal/energyUnitDecimal/energyUnitDecimal"
import { useFormatUnit } from "@/formatters"
import { StackedBarGraphTooltip } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph-tooltip"
import { StackedBarGraphTooltipRow } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph-tooltip-row"
import {
  type IntervalDisplayUnit,
  type IntervalStackedDataPoint,
  type IntervalStackedSeries,
} from "@/services/interval"
import { getDisplayListValue } from "@/utils/value"

import { Stack } from "@mui/material"

import { formatIntervalBucketLabel } from "./format-interval-bucket-label"
import { formatIntervalMeterSource } from "./format-interval-meter-source"
import type { IntervalGranularity } from "./interval-data-selection"

interface IntervalDataChartTooltipPayloadItem {
  color?: string
  dataKey?: number | string
  payload?: IntervalStackedDataPoint
  value?: number
}

interface IntervalDataChartTooltipProps {
  active?: boolean
  granularity: IntervalGranularity
  meterData?: IntervalStackedDataPoint[]
  meterSeries?: IntervalStackedSeries[]
  payload?: IntervalDataChartTooltipPayloadItem[]
  series: IntervalStackedSeries[]
  timeZone: string
  unit?: IntervalDisplayUnit
}

const contributingMeterLabels = (
  point: IntervalStackedDataPoint | undefined,
  meterSeries: IntervalStackedSeries[],
  source: string
): string[] => {
  if (!point) {
    return []
  }

  const labels: string[] = []
  const seen = new Set<string>()
  for (const item of meterSeries) {
    if (item.source !== source || typeof point[item.key] !== "number") {
      continue
    }
    if (seen.has(item.label)) {
      continue
    }
    seen.add(item.label)
    labels.push(item.label)
  }
  return labels
}

const hoveredMeterCodeValue = (
  dataKey: string,
  plottedSeries: IntervalStackedSeries[],
  meterSeries: IntervalStackedSeries[],
  meterPoint: IntervalStackedDataPoint | undefined
): string => {
  const plottedItem = plottedSeries.find((item) => item.key === dataKey)
  const meterItem = meterSeries.find((item) => item.key === dataKey)
  if (meterItem) {
    return plottedItem?.label ?? meterItem.label
  }

  return getDisplayListValue(
    contributingMeterLabels(
      meterPoint,
      meterSeries,
      plottedItem?.source ?? dataKey
    )
  )
}

export const IntervalDataChartTooltip: React.FC<
  IntervalDataChartTooltipProps
> = ({
  active,
  granularity,
  meterData,
  meterSeries,
  payload,
  series,
  timeZone,
  unit,
}) => {
  const { t } = useTranslation("sites")
  const { formatUnit } = useFormatUnit()
  const unitLabel = formatUnit(unit)
  const hovered = payload?.[0]
  const hoveredPoint = hovered?.payload
  const resolvedMeterSeries = meterSeries ?? series

  if (!active || !hovered || !hoveredPoint) {
    return null
  }

  const dataKey = String(hovered.dataKey ?? "")
  const hoveredSeries = series.find((item) => item.key === dataKey)
  const meterPoint = meterData?.find(
    (point) => point.xaxis === hoveredPoint.xaxis
  )

  return (
    <StackedBarGraphTooltip>
      <Stack spacing={1}>
        <StackedBarGraphTooltipRow
          label={t("intervalData.tooltip.date")}
          value={formatIntervalBucketLabel(
            granularity,
            hoveredPoint.xaxis,
            timeZone
          )}
        />
        <StackedBarGraphTooltipRow
          label={t("fields.meterCode.title", { ns: "common" })}
          swatchColor={hovered.color}
          value={hoveredMeterCodeValue(
            dataKey,
            series,
            resolvedMeterSeries,
            meterPoint ?? hoveredPoint
          )}
        />
        <StackedBarGraphTooltipRow
          label={t("intervalData.tooltip.usage")}
          value={
            <>
              <EnergyUnitDecimal
                useAccountingFormatWhenNegative={false}
                value={hovered.value}
              />
              {unitLabel ? ` ${unitLabel}` : null}
            </>
          }
        />
        <StackedBarGraphTooltipRow
          label={t("intervalData.tooltip.source")}
          value={formatIntervalMeterSource(hoveredSeries?.source, (key) =>
            t(key, { ns: "database" })
          )}
        />
      </Stack>
    </StackedBarGraphTooltip>
  )
}
